import React, { useState } from 'react';

const GrammarUnit2Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');
  const [showAnswers, setShowAnswers] = useState(false);

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 2 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">英文動詞時態概覽</h3>
        <p className="text-gray-300 mb-6 whitespace-pre-line">{content.overview.description}</p>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-600">
            <thead>
              <tr className="bg-gray-700">
                <th className="border border-gray-600 p-3 text-left">時態</th>
                <th className="border border-gray-600 p-3 text-left">現在式</th>
                <th className="border border-gray-600 p-3 text-left">過去式</th>
                <th className="border border-gray-600 p-3 text-left">未來式</th>
              </tr>
            </thead>
            <tbody>
              {content.overview.timelineData.map((row, index) => (
                <tr key={index} className="border-b border-gray-600">
                  <td className="border border-gray-600 p-3 font-semibold text-green-300">{row.tense}</td>
                  <td className="border border-gray-600 p-3 text-gray-300">{row.present}</td>
                  <td className="border border-gray-600 p-3 text-gray-300">{row.past}</td>
                  <td className="border border-gray-600 p-3 text-gray-300">{row.future}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTenseSection = (tenseKey, tenseData) => (
    <div className="bg-gray-700/50 rounded-lg p-4 mb-4">
      <h4 className="text-lg font-semibold mb-3 text-green-300">{tenseData.title}</h4>
      <p className="text-yellow-300 font-mono mb-3">{tenseData.structure}</p>
      
      <div className="space-y-4">
        {tenseData.sections.map((section, index) => (
          <div key={index} className="bg-gray-600/30 p-3 rounded">
            <h5 className="text-md font-semibold mb-2 text-purple-300">{section.name}</h5>
            {section.structure && (
              <p className="text-yellow-300 font-mono text-sm mb-2">{section.structure}</p>
            )}
            {section.negativeStructure && (
              <p className="text-red-300 font-mono text-sm mb-2">{section.negativeStructure}</p>
            )}
            {section.questionStructure && (
              <p className="text-blue-300 font-mono text-sm mb-2">{section.questionStructure}</p>
            )}
            {section.usage && (
              <p className="text-gray-300 text-sm mb-2">{section.usage}</p>
            )}
            {section.examples && (
              <div className="space-y-1">
                {section.examples.map((example, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="text-blue-300">{example}</p>
                  </div>
                ))}
              </div>
            )}
            {section.note && (
              <p className="text-orange-300 text-sm mt-2">{section.note}</p>
            )}
            {section.additionalExample && (
              <p className="text-blue-300 text-sm mt-2">{section.additionalExample}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTenses = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">時態詳解</h3>
        
        {renderTenseSection('pastSimple', content.pastSimple)}
        {renderTenseSection('pastContinuous', content.pastContinuous)}
        {renderTenseSection('pastPerfect', content.pastPerfect)}
        {renderTenseSection('pastPerfectContinuous', content.pastPerfectContinuous)}
        {renderTenseSection('futureSimple', content.futureSimple)}
        {renderTenseSection('futureContinuous', content.futureContinuous)}
        {renderTenseSection('futurePerfect', content.futurePerfect)}
        {renderTenseSection('futurePerfectContinuous', content.futurePerfectContinuous)}
        {renderTenseSection('presentSimple', content.presentSimple)}
      </div>
    </div>
  );

  const renderExercises = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">練習題</h3>
        
        <div className="space-y-6">
          {content.exercises.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">
                {section.name}
              </h4>
              
              <div className="space-y-4">
                {section.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-gray-300 mb-3">{question.question}</p>
                    <div className="space-y-2">
                      {Object.entries(question.options).map(([key, option]) => (
                        <div key={key} className="flex items-center">
                          <span className="text-yellow-300 font-mono mr-3">{key}.</span>
                          <span className="text-gray-300">{option}</span>
                        </div>
                      ))}
                    </div>
                    
                    {showAnswers && (
                      <div className="mt-3 bg-green-600/20 p-3 rounded border border-green-500/30">
                        <p className="text-green-300 font-semibold">
                          正確答案: {question.options[Object.keys(question.options)[question.answer]]}
                        </p>
                        <p className="text-gray-300 mt-2">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={() => setShowAnswers(!showAnswers)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {showAnswers ? '隱藏答案' : '顯示答案'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          時態概覽
        </button>
        <button
          onClick={() => setCurrentSection('tenses')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'tenses'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          時態詳解
        </button>
        <button
          onClick={() => setCurrentSection('exercises')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'exercises'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          練習題
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'tenses' && renderTenses()}
        {currentSection === 'exercises' && renderExercises()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit2Content;
